import axios from "axios";
import type { CampaignReminders, SaveReminderEntry } from "@/interfaces/reminders";
import { apiClient } from "@/lib/apiClient";
import { normalizeReminders } from "@/lib/reminders";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import type { RawCampaignReminders } from "../../../gfgf-api/src/interfaces/reminders";

/**
 * Fetch persisted reminder dates for a campaign and normalize them into
 * sent/upcoming buckets ready for the modal.
 */
export async function fetchReminders(campaignId: string): Promise<CampaignReminders> {
  const { data } = await apiClient.get<RawCampaignReminders>(`/campaigns/${campaignId}/reminders`);
  // Split in the campaign's zone, the one the server reads a save in — not the browser's.
  const timeZone = resolveScheduleTimeZone(data.scheduledTz);
  return {
    ...normalizeReminders(
      [data.reminder1Date, data.reminder2Date, data.reminder3Date, data.reminder4Date],
      new Date(),
      timeZone,
    ),
    timeZone,
    // Passed straight through: the window is the server's answer, not something to recompute here.
    effectiveSendDate: data.effectiveSendDate,
    expireDate: data.expireDate,
  };
}

/**
 * Delete a single persisted reminder slot from a campaign.
 * Throws on failure so the caller can surface the error.
 */
export async function deleteReminder(campaignId: string, reminderNumber: number): Promise<void> {
  try {
    await apiClient.delete(`/campaigns/${campaignId}/reminders/${reminderNumber}`);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to delete reminder. Please try again.");
  }
}

/**
 * Persist reminder entries for a campaign.
 * Throws on failure so the caller can surface the error to the user.
 */
export async function saveReminders(
  campaignId: string,
  entries: SaveReminderEntry[],
): Promise<void> {
  try {
    // No browser offset: the server reads each date+time in the campaign's own zone.
    await apiClient.post(`/campaigns/${campaignId}/reminders`, { reminders: entries });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw new Error("Failed to save reminders. Please try again.");
  }
}
